import React, { useState } from 'react';
import { useAuth } from '../../hooks/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Mail, Lock, User, Building, CheckCircle, Key, Shield, Heart, Users, Award } from 'lucide-react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import DarkVeil from '../../components/DarkVeil';

export const AuthPage: React.FC = () => {
  const { signIn, signUp, resetPassword, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    agency: '',
    responderType: 'Other' as 'Firefighter' | 'Police' | 'Other',
    accessCode: '',
    agreedToTerms: false
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);

    if (isSignUp && formData.password !== formData.confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (!formData.email || !formData.password) {
      setLocalError('Email and password are required');
      return;
    }

    if (isSignUp && !formData.agreedToTerms) {
      setLocalError('You must agree to the Terms and Privacy Policy to create an account');
      return;
    }

    setLoading(true);
    try {
      if (isSignUp) {
        await signUp(
          formData.email, 
          formData.password, 
          formData.displayName,
          formData.responderType,
          formData.agency,
          formData.accessCode,
          formData.agreedToTerms
        );
      } else {
        await signIn(formData.email, formData.password);
      }
      
      const to = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
      navigate(to, { replace: true });
      
    } catch (err: unknown) {
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);

    if (!formData.email) {
      setLocalError('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(formData.email);
      setSuccessMessage('Password reset email sent! Check your inbox.');
      setTimeout(() => {
        setShowForgotPassword(false);
        setSuccessMessage(null);
      }, 3000);
    } catch (err: unknown) {
      console.error('Password reset error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const features = [
    { icon: Shield, text: 'Evidence-based resilience training' },
    { icon: Heart, text: 'Mental health support tailored for first responders' },
    { icon: Users, text: 'Connect with your community' },
    { icon: Award, text: 'Track progress & earn achievements' }
  ];

  return (
    <div className="min-h-screen w-full relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 w-full h-full">
        <DarkVeil
          hueShift={210}
          noiseIntensity={0.02}
          scanlineIntensity={0.05}
          speed={1.2}
          scanlineFrequency={0.5}
          warpAmount={0.3}
          resolutionScale={1}
        />
        {/* Overlay for better readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-slate-900/60 to-black/70 backdrop-blur-[1px]" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col lg:flex-row">
        {/* Left Side - Branding (Hidden on mobile) */}
        <div className="hidden lg:flex lg:w-[55%] flex-col justify-center px-8 xl:px-16 2xl:px-24 text-white">
          <div className="max-w-2xl">
            {/* Logo */}
            <div className="mb-8 flex items-center space-x-3">
              <img 
                src="/icons/ggg-logo-512.png" 
                alt="GGG Logo" 
                className="w-16 h-16 object-contain drop-shadow-2xl"
              />
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Grit Growth & Gratitude</h1>
                <p className="text-teal-300 text-sm mt-1">Mental Fitness for Public Safety Members</p>
              </div>
            </div>

            {/* Tagline */}
            <h2 className="text-4xl xl:text-5xl font-bold leading-tight mb-6">
              Build Resilience.<br />
              Strengthen Your Mind.
            </h2>

            <p className="text-lg text-gray-200 mb-10 leading-relaxed">
              Join hundreds of first responders taking control of their mental wellness. 
              Science-backed training designed by responders, for responders.
            </p>

            {/* Feature List */}
            <div className="space-y-3">
              {features.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div 
                    key={idx}
                    className="flex items-center space-x-3 bg-black/30 backdrop-blur-sm rounded-xl p-3.5 border border-teal-500/30 hover:border-teal-400/50 hover:bg-black/40 transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-teal-600 flex items-center justify-center shadow-lg">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-gray-100 font-medium">{feature.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Side - Auth Form */}
        <div className="w-full lg:w-[45%] flex items-center justify-center p-4 sm:p-8 lg:p-8 xl:p-12">
          <div className="w-full max-w-md lg:max-w-2xl">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <img 
                src="/icons/ggg-logo-512.png" 
                alt="GGG Logo" 
                className="w-20 h-20 mx-auto mb-4 drop-shadow-2xl"
              />
              <h1 className="text-2xl font-bold text-white">Grit Growth & Gratitude</h1>
              <p className="text-teal-300 text-sm mt-1">Mental Fitness for Public Safety Members</p>
            </div>

            {/* Auth Card */}
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-teal-500/20 overflow-hidden">
              {/* Tabs - Hide when showing forgot password */}
              {!showForgotPassword && (
                <div className="grid grid-cols-2 bg-gray-50/80 backdrop-blur-sm p-1 m-4 rounded-xl">
                  <button
                    onClick={() => setIsSignUp(false)}
                    className={`py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
                      !isSignUp
                        ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-md'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => setIsSignUp(true)}
                    className={`py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
                      isSignUp
                        ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-md'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Sign Up
                  </button>
                </div>
              )}

              {/* Forgot Password Form */}
              {showForgotPassword && (
                <form onSubmit={handleForgotPassword} className="p-6 space-y-4">
                  <div className="text-center mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">Reset Password</h3>
                    <p className="text-sm text-gray-600 mt-2">
                      Enter your email and we'll send you a link to reset your password
                    </p>
                  </div>

                  {(error || localError) && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {error || localError}
                    </div>
                  )}

                  {successMessage && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                      {successMessage}
                    </div>
                  )}

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        name="email"
                        required
                        autoComplete="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="space-y-3 pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3.5 px-6 rounded-xl font-semibold focus:ring-4 focus:ring-teal-500/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg shadow-teal-500/30"
                    >
                      {loading ? (
                        <LoadingSpinner size="sm" color="white" />
                      ) : (
                        'Send Reset Link'
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setLocalError(null);
                        setSuccessMessage(null);
                      }}
                      className="w-full text-gray-600 hover:text-gray-900 py-2 font-medium transition-colors"
                    >
                      Back to Sign In
                    </button>
                  </div>
                </form>
              )}

              {/* Regular Sign In/Up Form */}
              {!showForgotPassword && (
                <form onSubmit={handleSubmit} className="p-6 pt-2 space-y-4">
                {(error || localError) && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start space-x-2">
                    <span className="flex-1">{error || localError}</span>
                  </div>
                )}

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      name="email"
                      required
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Password
                    </label>
                    {!isSignUp && (
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
                      >
                        Forgot Password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="password"
                      name="password"
                      required
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                {/* Sign Up Fields */}
                {isSignUp && (
                  <div className="space-y-4 pt-2">
                    {/* Two-column grid for desktop */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Confirm Password */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Confirm Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="password"
                            name="confirmPassword"
                            required
                            autoComplete="new-password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all"
                            placeholder="••••••••"
                          />
                        </div>
                        {formData.password !== formData.confirmPassword && formData.confirmPassword && (
                          <p className="text-red-600 text-xs mt-1.5 ml-1">Passwords do not match</p>
                        )}
                      </div>

                      {/* Display Name */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Full Name
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            name="displayName"
                            required
                            autoComplete="name"
                            value={formData.displayName}
                            onChange={handleInputChange}
                            className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all"
                            placeholder="John Doe"
                          />
                        </div>
                      </div>

                      {/* Responder Type */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Responder Type
                        </label>
                        <select
                          name="responderType"
                          value={formData.responderType}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all"
                        >
                          <option value="Firefighter">Firefighter</option>
                          <option value="Police">Police Officer</option>
                          <option value="Other">Other First Responder</option>
                        </select>
                      </div>

                      {/* Agency */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Agency/Department <span className="text-gray-400 font-normal">(Optional)</span>
                        </label>
                        <div className="relative">
                          <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            name="agency"
                            autoComplete="organization"
                            value={formData.agency}
                            onChange={handleInputChange}
                            className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all"
                            placeholder="Your department"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Access Code - Full width */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Department Access Code
                      </label>
                      <div className="relative">
                        <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="text"
                          name="accessCode"
                          value={formData.accessCode}
                          onChange={handleInputChange}
                          className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all"
                          placeholder="Enter code from admin"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5 ml-1">
                        Get this from your department administrator
                      </p>
                    </div>

                    {/* Terms and Conditions Checkbox */}
                    <div className="flex items-start space-x-2.5 pt-2 pb-1">
                      <input
                        type="checkbox"
                        id="agreedToTerms"
                        name="agreedToTerms"
                        checked={formData.agreedToTerms}
                        onChange={(e) => setFormData({ ...formData, agreedToTerms: e.target.checked })}
                        className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 focus:ring-2 cursor-pointer flex-shrink-0"
                        required
                      />
                      <label htmlFor="agreedToTerms" className="text-sm text-gray-700 leading-tight cursor-pointer">
                        I agree to the{' '}
                        <Link 
                          to="/terms" 
                          target="_blank"
                          className="text-teal-600 hover:text-teal-700 font-semibold underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Terms and Conditions
                        </Link>
                        {' '}and{' '}
                        <Link 
                          to="/privacy" 
                          target="_blank"
                          className="text-teal-600 hover:text-teal-700 font-semibold underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Privacy Policy
                        </Link>
                      </label>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || (isSignUp && formData.password !== formData.confirmPassword) || (isSignUp && !formData.agreedToTerms)}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3.5 px-6 rounded-xl font-semibold focus:ring-4 focus:ring-teal-500/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg shadow-teal-500/30 mt-6"
                >
                  {loading ? (
                    <LoadingSpinner size="sm" color="white" />
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      {isSignUp ? 'Create Account' : 'Sign In'}
                    </>
                  )}
                </button>

                {/* Footer Links */}
                <div className="text-center pt-4 space-y-3">
                  <p className="text-xs text-gray-500">
                    By continuing, you agree to our{' '}
                    <Link to="/terms" className="text-teal-600 hover:text-teal-700 font-medium">
                      Terms
                    </Link>
                    {' & '}
                    <Link to="/privacy" className="text-teal-600 hover:text-teal-700 font-medium">
                      Privacy Policy
                    </Link>
                  </p>
                  <Link 
                    to="/admin/auth" 
                    className="block text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Administrator Access →
                  </Link>
                </div>
              </form>
              )}
            </div>

            {/* Mobile Features */}
            <div className="lg:hidden mt-8 space-y-3">
              {features.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div 
                    key={idx}
                    className="flex items-center space-x-3 bg-black/30 backdrop-blur-md rounded-xl p-3 border border-teal-500/30"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white text-sm font-medium">{feature.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
